import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ImportProgressService } from '../../../core/services/import-progress.service';
import {
  ImportProgressState,
  IMPORT_STATUS_COMPLETED,
  IMPORT_STATUS_FINALIZING,
  IMPORT_STATUS_PROCESSING,
  IMPORT_STATUS_QUEUED,
  IMPORT_STATUS_VALIDATING,
} from '../../../core/models/import-progress';
import { PopupDialogService } from '../../popup-dialog/popup-dialog.service';
import {
  FAILED,
  IMPORT_PROGRESS_CALCULATING,
  IMPORT_PROGRESS_CLICK_TO_VIEW,
  IMPORT_PROGRESS_COMPLETED_MSG,
  IMPORT_PROGRESS_COMPLETED_TITLE,
  IMPORT_PROGRESS_CONTINUE_IN_BACKGROUND_BTN,
  IMPORT_PROGRESS_DONE_BTN,
  IMPORT_PROGRESS_FAILED_MSG,
  IMPORT_PROGRESS_FAILED_TITLE,
  IMPORT_PROGRESS_LEAVE_HINT,
  IMPORT_PROGRESS_PHASE_FINALIZING,
  IMPORT_PROGRESS_PHASE_VALIDATING,
  IMPORT_PROGRESS_SUBTITLE_FINALIZING,
  IMPORT_PROGRESS_SUBTITLE_PROCESSING,
  IMPORT_PROGRESS_SUBTITLE_QUEUED,
  IMPORT_PROGRESS_SUBTITLE_VALIDATING,
  IMPORT_PROGRESS_TIME_REMAINING_LABEL,
  IMPORT_PROGRESS_TITLE_FINALIZING,
  IMPORT_PROGRESS_TITLE_PROCESSING,
  IMPORT_PROGRESS_TITLE_QUEUED,
  IMPORT_PROGRESS_TITLE_VALIDATING,
  SUCCESS,
} from '../../constants';

/**
 * SYRC-7544: Import Progress Feedback — app-shell pop-up.
 *
 * Mounted at the app shell so it survives route changes / reloads (resumed via
 * ImportProgressService markers + getActiveImport). Bound to the shared, polled
 * import stream and demuxed by importJobId:
 *  - the first non-minimized import renders as a centered modal (with "Continue
 *    in background");
 *  - minimized imports render as compact bottom-right pills ("Click to view
 *    progress").
 *
 * Only percentComplete and the estimated time remaining are shown — no row
 * counts, chunk counts, or start time (PO decision: minimal UI). Terminal states
 * (Completed / Failed) are reported via PopupDialogService; a failure includes a
 * "download error spreadsheet" link built from errorFileUrl (AC 6 / AC 7).
 */
@Component({
  selector: 'app-import-progress-popup',
  templateUrl: './import-progress-popup.component.html',
  styleUrl: './import-progress-popup.component.css',
})
export class ImportProgressPopupComponent implements OnInit, OnDestroy {
  /** The one centered modal (first non-minimized import), or null. */
  readonly modalImport$: Observable<ImportProgressState | null>;
  /** Minimized imports shown as pills. */
  readonly pillImports$: Observable<ImportProgressState[]>;

  readonly continueInBackgroundLabel =
    IMPORT_PROGRESS_CONTINUE_IN_BACKGROUND_BTN;
  readonly clickToViewLabel = IMPORT_PROGRESS_CLICK_TO_VIEW;
  readonly leaveHint = IMPORT_PROGRESS_LEAVE_HINT;
  readonly timeRemainingLabel = IMPORT_PROGRESS_TIME_REMAINING_LABEL;
  readonly calculatingLabel = IMPORT_PROGRESS_CALCULATING;

  private readonly terminalSub: Subscription;
  // Import job ids whose terminal dialog has already been shown, so a late poll
  // or a visibility-resume can't fire the same dialog twice.
  private readonly terminalShown = new Set<string>();

  constructor(
    private importProgress: ImportProgressService,
    private popupDialogService: PopupDialogService,
  ) {
    this.modalImport$ = this.importProgress.activeImports$.pipe(
      map((imports) => imports.find((i) => !i.minimized) ?? null),
    );
    this.pillImports$ = this.importProgress.activeImports$.pipe(
      map((imports) => imports.filter((i) => i.minimized)),
    );
    this.terminalSub = this.importProgress.terminal$.subscribe((state) =>
      this.onImportTerminal(state),
    );
  }

  ngOnInit(): void {
    // Resume any import that was in flight before a reload / navigate-away (AC 2).
    void this.importProgress.init();
  }

  ngOnDestroy(): void {
    this.terminalSub.unsubscribe();
  }

  trackByImportJobId(_index: number, state: ImportProgressState): string {
    return state.importJobId;
  }

  continueInBackground(importJobId: string): void {
    this.importProgress.minimize(importJobId);
  }

  viewProgress(importJobId: string): void {
    this.importProgress.restore(importJobId);
  }

  // --- Presentation helpers ----------------------------------------------

  private isStatus(state: ImportProgressState, status: string): boolean {
    return (state.status ?? '').toLowerCase() === status.toLowerCase();
  }

  /** Status-specific modal title. */
  titleFor(state: ImportProgressState): string {
    if (this.isStatus(state, IMPORT_STATUS_QUEUED)) {
      return IMPORT_PROGRESS_TITLE_QUEUED;
    }
    if (this.isStatus(state, IMPORT_STATUS_VALIDATING)) {
      return IMPORT_PROGRESS_TITLE_VALIDATING(state.entityLabel);
    }
    if (this.isStatus(state, IMPORT_STATUS_FINALIZING)) {
      return IMPORT_PROGRESS_TITLE_FINALIZING;
    }
    // Processing (and any unknown non-terminal) -> "Importing <label> assets".
    return IMPORT_PROGRESS_TITLE_PROCESSING(state.entityLabel);
  }

  /** Status-specific modal subtitle. */
  subtitleFor(state: ImportProgressState): string {
    if (this.isStatus(state, IMPORT_STATUS_QUEUED)) {
      return IMPORT_PROGRESS_SUBTITLE_QUEUED(state.fileName);
    }
    if (this.isStatus(state, IMPORT_STATUS_VALIDATING)) {
      return IMPORT_PROGRESS_SUBTITLE_VALIDATING(state.fileName);
    }
    if (this.isStatus(state, IMPORT_STATUS_FINALIZING)) {
      return IMPORT_PROGRESS_SUBTITLE_FINALIZING;
    }
    return IMPORT_PROGRESS_SUBTITLE_PROCESSING(
      state.fileName,
      state.entityLabel,
    );
  }

  /** Optional phase label rendered next to the bar (Validating / Finalizing). */
  phaseFor(state: ImportProgressState): string | null {
    if (this.isStatus(state, IMPORT_STATUS_VALIDATING)) {
      return IMPORT_PROGRESS_PHASE_VALIDATING;
    }
    if (this.isStatus(state, IMPORT_STATUS_FINALIZING)) {
      return IMPORT_PROGRESS_PHASE_FINALIZING;
    }
    return null;
  }

  /** Queued shows no track at all (just "waiting to start"). */
  showTrack(state: ImportProgressState): boolean {
    return !this.isStatus(state, IMPORT_STATUS_QUEUED);
  }

  isIndeterminate(state: ImportProgressState): boolean {
    return state.barMode === 'indeterminate';
  }

  /** Whether to show the "Time remaining" line (only once Processing). */
  showTimeRemaining(state: ImportProgressState): boolean {
    return this.isStatus(state, IMPORT_STATUS_PROCESSING);
  }

  /** "Calculating…" until the first chunk completes, else "N min/sec" (AC 3). */
  timeRemainingText(state: ImportProgressState): string {
    const seconds = state.estimatedSecondsRemaining;
    if (seconds == null) {
      return this.calculatingLabel;
    }
    return this.formatDuration(seconds);
  }

  private formatDuration(totalSeconds: number): string {
    const s = Math.max(0, Math.round(totalSeconds));
    if (s < 60) {
      return `${s} second${s === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(s / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  // --- Terminal handling --------------------------------------------------

  private onImportTerminal(state: ImportProgressState): void {
    if (this.terminalShown.has(state.importJobId)) {
      return;
    }
    this.terminalShown.add(state.importJobId);

    // Remove the progress pop-up (modal/pill) immediately so only the terminal
    // outcome dialog is shown — otherwise both appear stacked on Completed/Failed.
    this.importProgress.dismiss(state.importJobId);

    if (this.isStatus(state, IMPORT_STATUS_COMPLETED)) {
      this.popupDialogService.openDialog(
        IMPORT_PROGRESS_COMPLETED_TITLE(state.entityLabel),
        IMPORT_PROGRESS_COMPLETED_MSG,
        SUCCESS,
        IMPORT_PROGRESS_DONE_BTN,
      );
      return;
    }

    // Failed: all-or-nothing, no rows saved. Offer the error spreadsheet link
    // (AC 6 / AC 7) via the dialog's `link` slot when a URL is present.
    this.popupDialogService.openDialog(
      IMPORT_PROGRESS_FAILED_TITLE(state.entityLabel),
      state.message || IMPORT_PROGRESS_FAILED_MSG,
      FAILED,
      IMPORT_PROGRESS_DONE_BTN,
      undefined,
      false,
      undefined,
      false,
      undefined,
      state.errorFileUrl ?? null,
    );
  }
}
